/**
 * The objects of a loaded glTF that Godot's importer makes a MeshInstance3D, a GeometryInstance3D,
 * and the surfaces each draws. GLTFLoader makes a mesh of one primitive a Mesh and a mesh of
 * several a Group of one Mesh per primitive, so a tag in `userData`, which a clone copies, marks
 * the object that stands for the glTF mesh.
 */
import { setVisualLayers } from '../../../r3f/visualLayers';
const MESH_INSTANCE_KEY = 'textsceneMeshInstance';
const READS_NO_LIGHTMAP_KEY = 'textsceneReadsNoLightmap';
/**
 * Tags each object under `root` that stands for a glTF node's mesh, from the parser's
 * `associations`. A primitive Mesh of a Group is the Group's surface, unless it is a node itself.
 */
export function tagMeshInstances(root, associations) {
    root.traverse((object) => {
        const association = associations.get(object);
        if (association?.meshes === undefined)
            return;
        const isPrimitive = association.nodes === undefined && isPrimitiveGroup(associations.get(object.parent));
        if (!isPrimitive)
            object.userData[MESH_INSTANCE_KEY] = true;
    });
}
/** Whether GLTFLoader built the object for a mesh of several primitives. */
function isPrimitiveGroup(association) {
    return association?.meshes !== undefined && association.primitives === undefined;
}
/** Whether Godot imports `object` as a MeshInstance3D. */
export function isMeshInstance(object) {
    return object.userData[MESH_INSTANCE_KEY] === true;
}
/**
 * Whether the importer unwrapped `object`'s glTF mesh and the previewer could not rebuild the
 * unwrap, so no LightmapGI lightmap fits it. A clone keeps the mark, as it lives in `userData`.
 */
export function readsNoLightmap(object) {
    return object.userData[READS_NO_LIGHTMAP_KEY] === true;
}
/** Marks `object` as `readsNoLightmap`. */
export function markReadsNoLightmap(object) {
    object.userData[READS_NO_LIGHTMAP_KEY] = true;
}
/** The surfaces a MeshInstance3D object draws, or none for any other object. */
export function meshInstanceSurfaces(object) {
    if (!isMeshInstance(object))
        return [];
    if (isSurface(object))
        return [object];
    return object.children.filter((child) => !isMeshInstance(child) && isSurface(child));
}
/**
 * Sets the render-layer mask of the MeshInstance3D `object` on it and on each surface it draws. A
 * node under it keeps its own mask, as `layers` is per instance.
 */
export function stampMeshInstanceLayers(object, layers) {
    setVisualLayers(object, layers);
    for (const surface of meshInstanceSurfaces(object))
        setVisualLayers(surface, layers);
}
function isSurface(object) {
    const { geometry, material } = object;
    return geometry !== undefined && material !== undefined;
}
//# sourceMappingURL=meshInstances.js.map